import React from 'react';
import { categoryList } from '../../common';

const NOT_FOUND = '/not-found.png';

/** Image URL from an ImageRef or a plain string (asesor / promo galleries). */
export function getImageUrl(imageRef) {
    if (!imageRef) return NOT_FOUND;
    if (typeof imageRef === 'string') return imageRef;
    return imageRef?.url || NOT_FOUND;
}

/** Image URL from an ImageRef, tolerant of the formats the promotor app has produced over time. */
export function getPromotorImageUrl(imageRef) {
    if (!imageRef) return NOT_FOUND;

    // If it's a string (direct URL or base64)
    if (typeof imageRef === 'string') {
        // Check if it's a valid URL or base64
        if (imageRef.startsWith('http') || imageRef.startsWith('data:image')) {
            return imageRef;
        }
        return NOT_FOUND;
    }

    // If it's an object with url property
    if (imageRef?.url) {
        return imageRef.url;
    }

    // If it's an object with base64 property (legacy format)
    if (imageRef?.base64) {
        return imageRef.base64;
    }

    // If it's still processing, return not found
    if (imageRef?.status === 'processing' || imageRef?.status === 'pending') {
        return NOT_FOUND;
    }

    return NOT_FOUND;
}

/** Display name for a category, resolved through the category catalog. */
export function categoryDisplayName(category) {
    return categoryList?.find((c) => c?.name === category?.category)?.value || category?.category;
}

/** Share of shelf (%) for a category after the visit. */
export function categoryShare(category) {
    return category?.totalFaces > 0 ? (category?.faces / category?.totalFaces) * 100 : 0;
}

const isProcessing = (img) => img?.status === 'processing' || img?.status === 'pending';

/** Before/after figures for one promotor category card. */
export function categoryBeforeAfter(category) {
    const shareBefore = category?.totalBefore > 0 ? (category?.facesBefore / category?.totalBefore) * 100 : 0;
    const shareAfter = categoryShare(category);
    const improvement = shareAfter - shareBefore;

    const rawImagesBefore = category?.imagesBefore || [];
    const rawImagesAfter = category?.imagesAfter || [];

    // Images that have valid URLs (processed successfully)
    const hasUrl = (img) => {
        const url = getPromotorImageUrl(img);
        return url && url !== NOT_FOUND;
    };

    return {
        shareBefore,
        shareAfter,
        improvement,
        hasImprovement: improvement > 0,
        rawImagesBefore,
        rawImagesAfter,
        imagesBefore: rawImagesBefore.filter(hasUrl),
        imagesAfter: rawImagesAfter.filter(hasUrl),
        // Images still processing show a placeholder
        hasProcessingBefore: rawImagesBefore.some(isProcessing),
        hasProcessingAfter: rawImagesAfter.some(isProcessing),
        hasImages: rawImagesBefore.length > 0 || rawImagesAfter.length > 0
    };
}

/** Data for the promotor "Trabajo Realizado" section. */
export function usePromotorImages(storeCheck) {
    const categories = React.useMemo(() => storeCheck?.categories || [], [storeCheck?.categories]);
    const ourSpaces = storeCheck?.ourSpaces || [];
    const theirSpaces = storeCheck?.theirSpaces || [];

    // Categories that have images
    const categoriesWithImages = categories.filter((cat) => cat?.imagesBefore?.length > 0 || cat?.imagesAfter?.length > 0);

    // Debug: log categories data to help troubleshoot image processing
    React.useEffect(() => {
        if (categories.length > 0) {
            console.log(
                'PromotorImages - Categories data:',
                categories.map((cat) => ({
                    category: cat.category,
                    imagesBefore: cat.imagesBefore?.length || 0,
                    imagesAfter: cat.imagesAfter?.length || 0,
                    beforeStatuses: cat.imagesBefore?.map((img) => img?.status || (img?.url ? 'has-url' : 'unknown')),
                    afterStatuses: cat.imagesAfter?.map((img) => img?.status || (img?.url ? 'has-url' : 'unknown'))
                }))
            );
        }
    }, [categories]);

    // Overall improvement (average across categories)
    const totalImprovement =
        categories.reduce((acc, cat) => {
            const before = cat?.totalBefore > 0 ? (cat?.facesBefore / cat?.totalBefore) * 100 : 0;
            const after = cat?.totalFaces > 0 ? (cat?.faces / cat?.totalFaces) * 100 : 0;
            return acc + (after - before);
        }, 0) / (categories.length || 1);

    return { categoriesWithImages, ourSpaces, theirSpaces, totalImprovement };
}
